import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import useGardenData from '../hooks/useGardenData';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Stack,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    MoreVert,
} from '@mui/icons-material';
import WeatherSummary from './shared/WeatherSummary';
import { LogEvent } from '../types/shared';
import ManualLogEntryModal from './ManualLogEntryModal';
import { Fab } from '@mui/material';
import { deleteManualLogEntry } from '../services/logService';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from './shared/Loading';

const Logbook: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user, userProfile, loading: gardenDataLoading } = useGardenData();
    const [events, setEvents] = useState<LogEvent[]>([]);
    const [weatherData, setWeatherData] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<LogEvent | null>(null);
    const [deletingEvent, setDeletingEvent] = useState<LogEvent | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuEvent, setMenuEvent] = useState<LogEvent | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const userLocationId = userProfile?.location?.toLowerCase().replace(/\s+/g, '_') || 'new_york';

    useEffect(() => {
        if (!user || gardenDataLoading) {
            return;
        }

        // Query for user-specific log events.
        const userEventsQuery = query(
            collection(db, 'log_events'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubUser = onSnapshot(userEventsQuery, (snapshot) => {
            const userEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LogEvent));
            setEvents(userEvents);
            setLoading(false);
        }, (error) => {
            console.error('Error loading user events:', error);
            setLoading(false);
        });

        // Query weather data for the user's location
        const weatherQuery = query(
            collection(db, 'weather', userLocationId, 'dates'),
            orderBy('date', 'desc')
        );

        const unsubWeather = onSnapshot(weatherQuery, (snapshot) => {
            const weatherMap: Record<string, any> = {};
            snapshot.forEach(doc => {
                const data = doc.data();
                weatherMap[data.date] = data;
            });
            setWeatherData(weatherMap);
        });

        return () => {
            unsubUser();
            unsubWeather();
        };
    }, [user, gardenDataLoading, userLocationId]);

    if (!user) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography>{t('logbook.please_log_in')}</Typography>
            </Container>
        );
    }

    if (loading) {
        return <LoadingScreen />;
    }

    // Group events by date
    const groupedEvents = events.reduce((groups, event) => {
        let dateKey = '';
        if (event.createdAt) {
            const d = (event.createdAt as any).toDate ? (event.createdAt as any).toDate() : (event.createdAt as Date);
            dateKey = d.toDateString();
        } else {
            dateKey = 'Unknown Date';
        }

        if (!groups[dateKey]) {
            groups[dateKey] = [];
        }
        groups[dateKey].push(event);
        return groups;
    }, {} as Record<string, LogEvent[]>);

    const dateGroups = Object.entries(groupedEvents).map(([dateKey, events]) => ({
        date: new Date(dateKey),
        events,
    }));

    // Sort groups by date descending just to be sure (though events were sorted)
    dateGroups.sort((a, b) => b.date.getTime() - a.date.getTime());

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, logEvent: LogEvent) => {
        setAnchorEl(event.currentTarget);
        setMenuEvent(logEvent);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuEvent(null);
    };

    const handleEditClick = () => {
        if (menuEvent) {
            setEditingEvent(menuEvent);
            setIsModalOpen(true);
        }
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        if (menuEvent) {
            setDeletingEvent(menuEvent);
        }
        handleMenuClose();
    };

    const confirmDelete = async () => {
        if (deletingEvent) {
            try {
                await deleteManualLogEntry(deletingEvent.id);
                setDeletingEvent(null);
            } catch (error) {
                console.error('Error deleting log entry:', error);
            }
        }
    };

    const groupedEventsByDate: { [key: string]: LogEvent[] } = {};
    events.forEach(event => {
        const date = event.createdAt instanceof Date
            ? event.createdAt
            : (event.createdAt && 'toDate' in event.createdAt)
                ? event.createdAt.toDate()
                : new Date(NaN);

        const dateStr = date.toDateString();
        if (!groupedEventsByDate[dateStr]) {
            groupedEventsByDate[dateStr] = [];
        }
        groupedEventsByDate[dateStr].push(event);
    });

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            pb: 10,
            pt: 4
        }}>
            <Container maxWidth="sm">
                {events.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="text.secondary">
                            {t('logbook.no_events')}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={4}>
                        {Object.keys(groupedEventsByDate).map(dateStr => {
                            const date = new Date(dateStr);
                            const otherEvents = groupedEventsByDate[dateStr];

                            const isUnknownDate = date.toString() === 'Invalid Date';
                            const displayDate = isUnknownDate
                                ? t('logbook.processing')
                                : date.toDateString() === new Date().toDateString()
                                    ? t('logbook.today')
                                    : date.toDateString() === new Date(Date.now() - 86400000).toDateString()
                                        ? t('logbook.yesterday')
                                        : date.toLocaleDateString(i18n.language, { weekday: 'long', month: 'short', day: 'numeric' });

                            // Get weather info for this date
                            // Format date as YYYY-MM-DD in local time to match weather collection keys
                            const isoDate = isUnknownDate ? '' : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                            const dayWeather = weatherData[isoDate];

                            return (
                                <Card key={isUnknownDate ? 'unknown-date' : date.toISOString()} elevation={0} sx={{
                                    borderRadius: 3,
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                }}>
                                    <CardContent sx={{ pb: '16px !important' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle2" className="serif-text" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>
                                                {displayDate}
                                            </Typography>

                                            {dayWeather && (
                                                <WeatherSummary
                                                    minTemp={dayWeather.minTemp}
                                                    maxTemp={dayWeather.maxTemp}
                                                    precipitation={dayWeather.precipitation}
                                                    description={dayWeather.description}
                                                />
                                            )}
                                        </Box>

                                        {otherEvents.length > 0 ? (
                                            <Box component="ul" sx={{ pl: 2, m: 0, textAlign: 'left' }}>
                                                {otherEvents.map((event) => (
                                                    <Box component="li" key={event.id} sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                    <Typography variant="body2" className="serif-text" color="text.primary">
                                                                        {event.description}
                                                                    </Typography>
                                                                </Box>
                                                                {event.isManual && (
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ p: 0.5, mt: -0.5, color: 'text.secondary' }}
                                                                        onClick={(e) => handleMenuOpen(e, event)}
                                                                    >
                                                                        <MoreVert fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                            {event.logImageUrl && (
                                                                <Box
                                                                    component="img"
                                                                    src={event.logImageUrl}
                                                                    alt="Event"
                                                                    sx={{
                                                                        width: '100%',
                                                                        maxWidth: 200,
                                                                        borderRadius: 2,
                                                                        mt: 0.5,
                                                                        boxShadow: 1,
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            opacity: 0.9
                                                                        }
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setPreviewImage(event.logImageUrl || null);
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                                                {t('logbook.no_activity')}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </Container>

            <ManualLogEntryModal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingEvent(null);
                }}
                userId={user.uid}
                initialData={editingEvent || undefined}
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEditClick}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('common.edit')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <Delete fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{t('common.delete')}</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog
                open={Boolean(deletingEvent)}
                onClose={() => setDeletingEvent(null)}
            >
                <DialogTitle>{t('logbook.delete_entry_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('logbook.delete_entry_message')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletingEvent(null)}>{t('common.cancel')}</Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="lg"
                onClick={() => setPreviewImage(null)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        m: 1,
                        maxHeight: 'calc(100% - 16px)',
                        maxWidth: 'calc(100% - 16px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }
                }}
            >
                {previewImage && (
                    <Box
                        component="img"
                        src={previewImage}
                        alt="Preview"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            cursor: 'zoom-out'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(null);
                        }}
                    />
                )}
            </Dialog>

            <Fab
                color="primary"
                aria-label="add"
                sx={{
                    position: 'fixed',
                    bottom: 100,
                    right: 16,
                    boxShadow: 4,
                    zIndex: 1000
                }}
                onClick={() => setIsModalOpen(true)}
            >
                <Add />
            </Fab>
        </Box >
    );
};

export default Logbook;
